import React, { useEffect, useMemo } from 'react'
import { AnalyticsDateRange } from '../models/AnalyticsDateRange'
import { DatePicker, Dropdown, Input, Text } from '@wix/design-system';
import { Adjust } from '@wix/wix-ui-icons-common'

interface Props {
    dateRange: AnalyticsDateRange;
    setDateRange: (dateRange: AnalyticsDateRange) => void;
    simplified?: boolean;
}


type DateRangeType = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'custom';
const DATE_RANGE_ORDER: DateRangeType[] = ['today', 'yesterday', 'last7days', 'last30days', 'custom'];
const DATE_RANGE_LABELS: Record<DateRangeType, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    last7days: 'Last 7 days',
    last30days: 'Last 30 days',
    custom: 'Custom',
};
interface DateRange {
    from: Date;
    to: Date;
}

function DateRangePicker(props: Props) {
    const globalDateRange = props.dateRange;
    const setGlobalDateRange = props.setDateRange;

    const [dateRangeType, setDateRangeType] = React.useState<DateRangeType>('last7days');
    const [dateRange, setDateRange] = React.useState<DateRange>({ from: globalDateRange.from, to: globalDateRange.to });
    const [prevDateRange, setPrevDateRange] = React.useState<DateRange>({ from: globalDateRange.compareFrom, to: globalDateRange.compareTo });


    const invalidRange = useMemo(() => dateRange.to.getTime() < dateRange.from.getTime(), [dateRange]);

    useEffect(() => {
        if (invalidRange) {
            return;
        }
        setGlobalDateRange({ ...dateRange, compareFrom: prevDateRange.from, compareTo: prevDateRange.to });
    }, [dateRange, prevDateRange, invalidRange, setGlobalDateRange]);


    useEffect(() => {
        if (dateRangeType === 'custom') {
            return;
        }
        const to = new Date();
        to.setHours(0, 0, 0, 0);
        to.setDate(to.getDate() + 1);

        const from = new Date(to);
        to.setSeconds(to.getSeconds() - 1);

        switch (dateRangeType) {
            case 'today':
                from.setDate(from.getDate() - 1);
                break;
            case 'yesterday':
                from.setDate(from.getDate() - 2);
                to.setDate(to.getDate() - 1);
                break;
            case 'last7days':
                from.setDate(from.getDate() - 7);
                break;
            case 'last30days':
                from.setDate(from.getDate() - 30);
                break;
        }

        setDateRange({ from, to });

    }, [dateRangeType, setDateRange])

    useEffect(() => {
        if (invalidRange) {
            return;
        }
        const dateDiff = dateRange.to.getTime() - dateRange.from.getTime();
        const diffDays = Math.ceil(dateDiff / (1000 * 60 * 60 * 24));

        const prevTo = new Date(dateRange.to);
        const prevFrom = new Date(dateRange.from);
        prevTo.setDate(prevTo.getDate() - diffDays);
        prevFrom.setDate(prevFrom.getDate() - diffDays);

        setPrevDateRange({ from: prevFrom, to: prevTo });
    }, [dateRange, setPrevDateRange, invalidRange])


    const finalRange = DATE_RANGE_ORDER.filter(key => !props.simplified || key !== 'custom');
    return <div className='d-flex align-items-center gap'>
        <Dropdown
            placeholder="Select language"
            prefix={
                <Input.IconAffix>
                    <Adjust />
                </Input.IconAffix>
            }
            options={finalRange.map(key => ({
                id: key,
                value: DATE_RANGE_LABELS[key],
            }))}
            selectedId={dateRangeType}
            onSelect={({ id }) => setDateRangeType(id as DateRangeType)}
        />

        {!props.simplified && <>
            <div className='d-flex align-items-center'>
                <DatePicker
                    value={dateRange.from}
                    readOnly={dateRangeType !== 'custom'}
                    onChange={(date: Date) => setDateRange({ ...dateRange, from: date })}
                    status={invalidRange ? 'error' : undefined}
                />
                <Text size='small' className='m-1'>-</Text>
                <DatePicker
                    value={dateRange.to}
                    readOnly={dateRangeType !== 'custom'}
                    onChange={(date: Date) => setDateRange({ ...dateRange, to: date })}
                    status={invalidRange ? 'error' : undefined}
                />
            </div>

            <Text size='small'>Compare to {prevDateRange.from.toLocaleDateString()} - {prevDateRange.to.toLocaleDateString()}</Text>
        </>}
    </div>
}

export default DateRangePicker
